import React from 'react';
import { DayOfWeek } from '@/proto/logiblocks/logiblocks';
import { getDayOfWeekNarrowLabel } from '../dateTimeUtils';
import styles from './DayOfWeekPicker.module.css';

const DAYS = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

interface DayOfWeekPickerProps {
  selectedDays: DayOfWeek[];
  onChange: (days: DayOfWeek[]) => void;
  disabled?: boolean;
  whiteInputs?: boolean;
  /** Allows deselecting the last day (the caller disables its own Submit at
   * zero days). Defaults to false: at least one day stays selected. */
  allowEmpty?: boolean;
}

/**
 * Multi-select button group for picking one or more days of the week.
 * At least one day must remain selected — deselecting the last day is a
 * no-op — unless `allowEmpty` opts out.
 */
export const DayOfWeekPicker: React.FC<DayOfWeekPickerProps> = ({
  selectedDays,
  onChange,
  disabled = false,
  whiteInputs = false,
  allowEmpty = false,
}) => {
  const handleToggle = (day: DayOfWeek) => {
    if (disabled) {
      return;
    }

    const newDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day].sort((a, b) => a - b);

    if (newDays.length === 0 && !allowEmpty) {
      return;
    }

    onChange(newDays);
  };

  return (
    <div
      className={styles.container}
      role='group'
      aria-label='Days of the week'
    >
      {DAYS.map((day) => {
        const selected = selectedDays.includes(day);
        return (
          <button
            key={day}
            type='button'
            role='checkbox'
            aria-checked={selected}
            aria-label={getDayOfWeekNarrowLabel(day)}
            className={[
              styles.day,
              selected ? styles.selected : '',
              disabled ? styles.disabled : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleToggle(day)}
            disabled={disabled}
            style={{
              backgroundColor: whiteInputs ? 'var(--surface-container)' : '',
            }}
          >
            {getDayOfWeekNarrowLabel(day)}
          </button>
        );
      })}
    </div>
  );
};
